// ---------------------------------------------------------------------------
// Redesign A/B experiment – client state + assignment logic
// ---------------------------------------------------------------------------
// Preference test for the portal redesign. Two concepts are tracked separately:
//
//   • bucket          – sticky cohort, set ONCE and never changed.
//                       control = existing members, test = new members.
//                       This is what cohort analysis groups on (intent-to-treat).
//   • active_version  – what the member is viewing RIGHT NOW (old|new). Set at
//                       assignment (test members split across the two arms,
//                       control on old), then changed by the sidebar toggle.
//                       The headline "which version do members choose" metric
//                       falls out of this field + the switch-event history.
//
// The API owns assignment (test_assignments, see its redesignAssignment/
// service.js): a member has to be in an arm from signup rather than from first
// login, or analysis can't tell "old design" from "hasn't logged in yet", and
// new members go to whichever arm is smaller — which no single client can know.
//
// localStorage is the fast per-device cache; the Klaviyo profile is still
// mirrored for segments and flows. Every switch and the initial assignment are
// also emitted as Klaviyo events so the before/after AOV & conversion analysis
// can be built downstream.
//
// SCAFFOLD NOTE: the actual redesigned pages do not exist yet. `active_version`
// is wired end-to-end (assignment, toggle, tracking, feedback) so that when the
// new pages land they only need to branch on `isTestVersionActive()`.

import {
  setRedesignProfile,
  getRedesignAssignment,
  setRedesignAssignmentVersion,
  trackRedesignAssigned,
  trackRedesignSwitch,
  submitFeedback,
} from './api';
import { getCurrentUserId } from '../utils/feedbackStorage';

// Members whose account was created before this instant → control (existing),
// on/after → test (new). ⚠️ The API applies the cutoff now (LAUNCH_DATE in
// redesignAssignment/service.js); this copy is only for portal code that needs
// to explain the rule, so change the API's if you're moving the real cutoff.
export const REDESIGN_LAUNCH_DATE = '2026-08-04T00:00:00Z';

// Wider release, from midnight ET on Sep 25 2026 — ROLLOUT_START in the API's
// redesignAssignment/service.js, which is what actually applies it. From then
// new members default to the new design, and existing members are split into
// an 'invite' arm, who get the switcher, and a 'holdout' arm, who don't. The
// API only returns a rolloutArm once the rollout has started, so the portal
// needs no clock of its own.
export const ROLLOUT_ARM = { INVITE: 'invite', HOLDOUT: 'holdout' };

// Kill switch — while true, everyone stays on the old design, the switcher is
// hidden, and nobody is bucketed. Flip on to pull the redesign without a revert.
export const FORCE_OLD_DESIGN = false;

export const BUCKET = { CONTROL: 'control', TEST: 'test' };
export const VERSION = { OLD: 'old', NEW: 'new' };

// localStorage keys
const KEY_BUCKET = 'redesign_bucket';
const KEY_VERSION = 'redesign_active_version';
const KEY_OVERRIDE = 'redesign_design_override';
const KEY_FEEDBACK_GIVEN = 'redesign_feedback_given';
const KEY_ROLLOUT_ARM = 'redesign_rollout_arm';
const KEY_INVITE_DISMISSED = 'redesign_invite_modal_dismissed';

// Fired whenever the active version changes so any mounted component
// (sidebar, pages) can re-render against the new value. Mirrors the existing
// `sidebar-toggle` / `wg-active-subscriptions-changed` conventions.
export const REDESIGN_VERSION_EVENT = 'wg-redesign-version-changed';

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage unavailable (private mode / quota) — non-fatal */
  }
};

const safeRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* localStorage unavailable — non-fatal */
  }
};

export const getBucket = () => safeGet(KEY_BUCKET);
export const getRolloutArm = () => safeGet(KEY_ROLLOUT_ARM);

// Who gets the design switcher: new members (the test bucket), and existing
// members invited by the wider rollout.
export const canSwitchDesign = (bucket = getBucket(), rolloutArm = getRolloutArm()) =>
  !FORCE_OLD_DESIGN && (bucket === BUCKET.TEST || rolloutArm === ROLLOUT_ARM.INVITE);

// The one-time "try the new design" modal for invited existing members. Once
// closed (or acted on) it never reopens on this device — the sidebar switcher
// stays as the way in.
export const hasDismissedInviteModal = () => safeGet(KEY_INVITE_DISMISSED) === 'true';
export const dismissInviteModal = () => safeSet(KEY_INVITE_DISMISSED, 'true');

// Device-level pin set by ?design=old|new. Layered on top of the cached
// assignment at read time rather than written into it, so KEY_VERSION always
// holds what the server last said and ?design=reset can fall straight back to
// it. It also makes the pin immune to a late assignment response landing after
// the switch — that response can only rewrite KEY_VERSION, which this shadows.
export const getDesignOverride = () => {
  const v = safeGet(KEY_OVERRIDE);
  return v === VERSION.OLD || v === VERSION.NEW ? v : null;
};

export const getActiveVersion = () => getDesignOverride() || safeGet(KEY_VERSION);
export const isTestVersionActive = () =>
  !FORCE_OLD_DESIGN && getActiveVersion() === VERSION.NEW;
export const isAssigned = () => !!getBucket();
export const hasGivenFeedback = () => safeGet(KEY_FEEDBACK_GIVEN) === 'true';

// Seed the local cache from a known (bucket, activeVersion) pair and notify any
// mounted listeners so the UI re-renders against it.
const applyAssignment = (bucket, activeVersion, rolloutArm = null) => {
  safeSet(KEY_BUCKET, bucket);
  safeSet(KEY_VERSION, activeVersion);
  if (rolloutArm) safeSet(KEY_ROLLOUT_ARM, rolloutArm);
  else safeRemove(KEY_ROLLOUT_ARM);
  window.dispatchEvent(
    new CustomEvent(REDESIGN_VERSION_EVENT, { detail: { activeVersion, bucket, rolloutArm } })
  );
};

/**
 * Resolve the member's redesign assignment at login — one call to the API,
 * which returns the stored assignment or creates it. Falls back to this
 * device's cache if the API is unreachable.
 *
 * Deliberately does NOT read the Klaviyo profile first. That read only ever
 * fed two things — a re-roll guard for members assigned before
 * test_assignments existed (all since backfilled into it), and a "is this
 * their first assignment" flag the API now answers directly with `created`.
 * Inferring it from Klaviyo re-fired the assignment event whenever that read
 * failed, and it cost every member a Klaviyo round trip before first paint.
 *
 * RedesignSwitcher and Overview each mount useRedesignExperiment, so callers
 * share whichever read is in flight (see `ensureAssignment` below): two
 * concurrent reads meant the slower response could land after a switch and
 * re-apply the version the server held when it was issued.
 *
 * @param {Object} params
 * @param {string} params.email        – required for server sync + tracking
 * @param {string} [params.createdAt]  – member account creation ISO date
 *                                        (Recharge customer.created_at), which
 *                                        decides control vs test
 * @returns {Promise<{ bucket: string, activeVersion: string, rolloutArm: string|null, isNew: boolean }|null>}
 */
const resolveAssignment = async ({ email, createdAt } = {}) => {
  if (!email) return null;

  // Frozen pre-launch: don't bucket or track anyone while the experiment is
  // switched off, or we'd stamp members with a cohort nobody saw and the API
  // would then honour it forever once we do launch.
  if (FORCE_OLD_DESIGN) return null;

  const assignment = await getRedesignAssignment(email, { signupDate: createdAt });

  // API unreachable — don't invent an assignment, or a local coin flip would
  // disagree with what the server records later and the member would flip
  // designs on their next load. Keep the cached view and retry next time.
  if (!assignment) {
    const cachedBucket = getBucket();
    if (!cachedBucket) return null;
    return {
      bucket: cachedBucket,
      activeVersion: getActiveVersion() || VERSION.OLD,
      rolloutArm: getRolloutArm(),
      isNew: false,
    };
  }

  const { bucket, activeVersion, rolloutArm, created } = assignment;
  // Against the cached server view, not getActiveVersion(): a ?design= pin is
  // this device's own doing and mustn't count as the profile being out of step,
  // or we'd mirror the pre-switch version back over it.
  const staleLocally = safeGet(KEY_VERSION) !== activeVersion;

  applyAssignment(bucket, activeVersion, rolloutArm);

  // Mirror to Klaviyo so segments and flows keep working off the same cohort.
  // Every switch already mirrors itself, so this only needs to fire when the
  // member is new to the test or this device was out of step (a switch made
  // elsewhere, or an earlier mirror that failed). Fire-and-forget: both calls
  // swallow their own errors so login/render is never blocked.
  if (created || staleLocally) {
    setRedesignProfile(email, { bucket, activeVersion });
  }

  if (created) {
    trackRedesignAssigned(email, {
      bucket,
      active_version: activeVersion,
      reason: bucket === BUCKET.TEST ? 'new_member_after_launch' : 'existing_member_before_launch',
      assigned_at: new Date().toISOString(),
    });
  }

  return { bucket, activeVersion, rolloutArm: rolloutArm || null, isNew: created };
};

// Only ever one read in flight; concurrent callers share it and settle together.
let inFlightAssignment = null;

export const ensureAssignment = (params = {}) => {
  if (!inFlightAssignment) {
    inFlightAssignment = resolveAssignment(params).finally(() => {
      inFlightAssignment = null;
    });
  }
  return inFlightAssignment;
};

/**
 * Toggle to the other version (or set an explicit one). Updates the local
 * cache, notifies listeners, records the switch against the API assignment,
 * mirrors to the Klaviyo profile, and emits a switch event carrying from/to so
 * downstream can measure who moved and when. Only the CURRENT version moves —
 * the assigned arm stays put on both sides, so switching never skews the
 * balancer.
 *
 * @param {string} email
 * @param {string} [next] – explicit target version; defaults to the opposite
 *                          of the current one.
 * @param {string} [source] – where the switch came from ('sidebar' | 'url' | 'modal'),
 *                            recorded with the switch server-side.
 */
export const setActiveVersion = (email, next, source) => {
  const current = getActiveVersion() || VERSION.OLD;
  const target =
    next || (current === VERSION.NEW ? VERSION.OLD : VERSION.NEW);

  if (target === current) return target;

  safeSet(KEY_VERSION, target);
  // Move the pin with the switch when one is set, or it would shadow the new
  // version and the toggle would appear to do nothing.
  if (getDesignOverride()) safeSet(KEY_OVERRIDE, target);

  window.dispatchEvent(
    new CustomEvent(REDESIGN_VERSION_EVENT, {
      detail: { activeVersion: target, bucket: getBucket() },
    })
  );

  if (email) {
    setRedesignAssignmentVersion(email, target, source);
    setRedesignProfile(email, { activeVersion: target });
    trackRedesignSwitch(email, {
      bucket: getBucket(),
      from: current,
      to: target,
      switched_at: new Date().toISOString(),
    });
  }

  return target;
};

/**
 * Apply a `?design=old|new` switch: a real switch (server assignment, Klaviyo
 * profile + switch event, exactly like the sidebar toggle) that is also pinned
 * on this device, so it survives dropping the parameter.
 *
 * The pin is what makes it stick for CONTROL members. They have no
 * test_assignments row until they switch, and the assignment read reasserts
 * `old` on every load, so before this the override lasted only as long as the
 * parameter was in the URL.
 */
export const applyDesignOverride = (email, target) => {
  if (target !== VERSION.OLD && target !== VERSION.NEW) return null;

  // Before the pin is written, so `current` is the version they're actually on
  // and a repeat load with the parameter still in the URL no-ops instead of
  // re-firing the switch event.
  const result = setActiveVersion(email, target, 'url');
  safeSet(KEY_OVERRIDE, target);
  return result;
};

/**
 * Drop the device pin (`?design=reset`) and fall back to the assignment the
 * server last handed us, which KEY_VERSION has been holding all along.
 */
export const clearDesignOverride = () => {
  if (!getDesignOverride()) return null;
  safeRemove(KEY_OVERRIDE);
  const activeVersion = getActiveVersion();
  window.dispatchEvent(
    new CustomEvent(REDESIGN_VERSION_EVENT, { detail: { activeVersion, bucket: getBucket() } })
  );
  return activeVersion;
};

/**
 * Record the one-time sidebar feedback (1–5 rating + optional comment).
 * Marks feedback as given locally so the widget hides afterwards.
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {number} params.rating   – 1–5
 * @param {string} [params.comment]
 */
export const submitRedesignFeedback = async ({ email, rating, comment = '' } = {}) => {
  if (!email || !rating) return null;

  safeSet(KEY_FEEDBACK_GIVEN, 'true');
  window.dispatchEvent(new CustomEvent(REDESIGN_VERSION_EVENT, {
    detail: { activeVersion: getActiveVersion(), bucket: getBucket(), feedbackGiven: true },
  }));

  // Persist as website feedback (it is website feedback) rather than a bespoke
  // Klaviyo event. The design version the member is viewing is folded into the
  // category so it stays filterable, e.g. "Redesign (new)".
  const version = getActiveVersion();
  return submitFeedback('website', {
    userId: getCurrentUserId(),
    email,
    overallRating: rating,
    category: version ? `Redesign (${version})` : 'Redesign',
    comment,
  }).catch((err) => {
    console.error('Failed to submit redesign feedback', err);
    return null;
  });
};
